import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { moderatorAPI, handleAPIError, validateModeratorData } from '../utils/moderatorAPI';

export const useModerators = () => {
  const [moderators, setModerators] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Fetch all moderators
  const fetchModerators = useCallback(async () => {
    setLoading(true);
    try {
      const response = await moderatorAPI.getModerators();
      console.log(response, "moderator response");

      if (response.response === 'success') {
        setModerators(response.data || []);
      } else {
        message.error(response.msg || 'Failed to fetch moderators');
      }
    } catch (error) {
      handleAPIError(error, 'Failed to fetch moderators');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch single moderator details
  const fetchModerator = useCallback(async (moderatorId) => {
    try {
      const response = await moderatorAPI.getModerator(moderatorId);
      console.log('fetchModerator API response:', response);
      
      // Check if response has the expected structure
      if (response && response.data) {
        return response.data;
      } else if (response) {
        // Sometimes the API might return data directly without wrapping
        return response;
      }
      
      return null;
    } catch (error) {
      handleAPIError(error, 'Failed to fetch moderator details');
      return null;
    }
  }, []);

  // Fetch available modules
  const fetchAvailableModules = useCallback(async () => {
    try {
      const response = await moderatorAPI.getAvailableModules();
      if (response.response === 'success') {
        setAvailableModules(response.data || []);
      } else {
        message.error('Failed to fetch available modules');
      }
    } catch (error) {
      handleAPIError(error, 'Failed to fetch available modules');
    }
  }, []);

  // Fetch users for selection
  const fetchUsers = useCallback(async (searchTerm = '') => {
    try {
      const response = await moderatorAPI.getUsers(searchTerm);

      // Filter out users who are already moderators
      const existingModeratorIds = new Set(moderators.map(m => m.account));
      const availableUsers = response.results?.filter(user => !existingModeratorIds.has(user.id)) || [];
      setUsers(availableUsers);
    } catch (error) {
      handleAPIError(error, 'Failed to fetch users');
    }
  }, [moderators]);

  // Add new moderator
  const addModerator = useCallback(async (moderatorData) => {
    // Validate data
    const validationErrors = validateModeratorData(moderatorData);
    if (validationErrors.length > 0) {
      validationErrors.forEach(error => message.error(error));
      return false;
    }

    setModalLoading(true);
    try {
      const response = await moderatorAPI.addModerator(moderatorData);
      if (response.response === 'success') {
        message.success('Moderator added successfully');
        await fetchModerators();
        return true;
      } else {
        message.error(response.msg || 'Failed to add moderator');
        return false;
      }
    } catch (error) {
      handleAPIError(error, 'Failed to add moderator');
      return false;
    } finally {
      setModalLoading(false);
    }
  }, [fetchModerators]);

  // Update moderator
  const updateModerator = useCallback(async (moderatorId, moderatorData) => {
    // Validate data
    const validationErrors = validateModeratorData(moderatorData);
    if (validationErrors.length > 0) {
      validationErrors.forEach(error => message.error(error));
      return false;
    }

    setModalLoading(true);
    try {
      const response = await moderatorAPI.updateModerator(moderatorId, moderatorData);
      if (response) {
        message.success('Moderator updated successfully');
        await fetchModerators();
        return true;
      } else {
        message.error(response.msg || 'Failed to update moderator');
        return false;
      }
    } catch (error) {
      handleAPIError(error, 'Failed to update moderator');
      return false;
    } finally {
      setModalLoading(false);
    }
  }, [fetchModerators]);

  // Remove moderator
  const removeModerator = useCallback(async (moderatorId) => {
    try {
      const response = await moderatorAPI.removeModerator(moderatorId);
      if (response.response === 'success') {
        message.success('Moderator removed successfully');
        await fetchModerators();
        return true;
      } else {
        message.error(response.msg || 'Failed to remove moderator');
        return false;
      }
    } catch (error) {
      handleAPIError(error, 'Failed to remove moderator');
      return false;
    }
  }, [fetchModerators]);

  // Toggle moderator status
  const toggleModeratorStatus = useCallback(async (moderator) => {
    try {
      const response = await moderatorAPI.toggleModeratorStatus(moderator.id);
      if (response.response === 'success') {
        message.success(`Moderator ${moderator.is_active ? 'deactivated' : 'activated'} successfully`);
        await fetchModerators();
        return true;
      } else {
        message.error('Failed to update moderator status');
        return false;
      }
    } catch (error) {
      handleAPIError(error, 'Failed to update moderator status');
      return false;
    }
  }, [fetchModerators]);

  // Initialize data on mount
  useEffect(() => {
    fetchModerators();
    fetchAvailableModules();
  }, [fetchModerators, fetchAvailableModules]);

  // Fetch users when moderators change
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Statistics
  const statistics = {
    total: moderators.length,
    active: moderators.filter(m => m.is_active).length,
    inactive: moderators.filter(m => !m.is_active).length,
    totalModules: availableModules.length
  };

  return {
    // Data
    moderators,
    availableModules,
    users,
    statistics,

    // Loading states
    loading,
    modalLoading,

    // Actions
    addModerator,
    updateModerator,
    removeModerator,
    toggleModeratorStatus,
    fetchModerators,
    fetchModerator,
    fetchUsers,

    // Utilities
    refreshData: () => {
      fetchModerators();
      fetchAvailableModules();
      fetchUsers();
    }
  };
};